// Slack/Google-Chat hybrid message list. Day separators, author grouping,
// markdown bubbles, hover reply, thread count chip, attachment grid.

import { groupMessages } from '../../functions/chat/chatUtils.js'
import { applyFilters } from '../../functions/chat/messageScans.js'
import { renderMarkdown } from '../../functions/chat/renderMarkdown.js'
import { setupHoverActions } from '../../functions/chat/setupHoverActions.js'
import { getSDK } from '../../functions/sdk.js'

// Credit metering for chat-prefill AI now happens server-side. The
// edge function that runs the AI prompt knows the actual token count
// and burns there — see server/packages/credit-meter.

// How many messages to show initially per channel. Scrolling to the top
// loads the next page. Keeps initial render fast even for channels with
// thousands of messages.
const _PAGE_SIZE = 50

// Visible count per channel is stored in root.chatVisibleCounts so
// changes trigger reactive re-renders of the message list.
const _getVisible = (root, channelId) =>
  (root.chatVisibleCounts && root.chatVisibleCounts[channelId]) || _PAGE_SIZE

export const ChatMessageList = {
  hide: (el, s) =>
    !(s && s.root && (s.root.activeTab || 'conversation') === 'conversation'),
  flex: '1 1 0',
  flow: 'y',
  padding: 'B B1 B1 B1',
  gap: 'Z1',
  overflow: 'auto',
  minHeight: '0',
  position: 'relative',

  onCreate: (el, s) => {
    if (!el || !el.node) return
    const container = el.node

    // ── Scroll to bottom on first render ──
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (!container) return
        container.style.scrollBehavior = 'auto'
        container.scrollTop = container.scrollHeight
        container.style.scrollBehavior = ''
      })
    )

    // Delegated bubble-hover → shared ChatHoverActions (mounted at ChatMain
    // level, position: fixed). Keeps actions out of this scroll container.
    setupHoverActions(container)
  },

  onUpdate: (el, s) => {
    if (!el || !el.node) return
    const root = s && s.root
    if (!root) return
    const channelId = root.activeChannelId || ''
    const map = root.messagesByChannel || {}
    const list = map[channelId] || []
    const sig = `${channelId}:${list.length}`
    const channelChanged = el._lastChannel !== channelId
    const countGrew = sig !== el._lastSig
    if (!channelChanged && !countGrew) return
    el._lastSig = sig
    if (channelChanged) {
      root.update({
        chatVisibleCounts: {
          ...(root.chatVisibleCounts || {}),
          [channelId]: _PAGE_SIZE
        }
      })
      el._lastChannel = channelId
    }
    const n = el.node
    const nearBottom =
      channelChanged || n.scrollHeight - n.scrollTop - n.clientHeight < 150
    if (nearBottom) {
      // Double rAF ensures children have rendered before scrolling.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (!el.node) return
          el.node.style.scrollBehavior = 'auto'
          el.node.scrollTop = el.node.scrollHeight
          el.node.style.scrollBehavior = ''
        })
      )
    }
  },

  Empty: {
    if: (el, s) => {
      const root = s && s.root
      if (!root) return true
      if (!root.activeChannelId) return true
      const map = root.messagesByChannel || {}
      const list = (map[root.activeChannelId] || []).filter(
        (m) => !m.threadRoot
      )
      return list.length === 0
    },
    flow: 'y',
    align: 'center center',
    flex: '1',
    color: 'caption',
    fontSize: 'A',
    text: (el, s) =>
      s.root.activeChannelId
        ? 'No messages yet — say something.'
        : 'Pick a channel from the sidebar.'
  },

  // "Load earlier messages" sentinel — shown when there are more messages
  // than the current visible window. Clicking or scrolling into view
  // expands the window by one page.
  LoadMore: {
    hide: (el, s) => {
      const root = s && s.root
      if (!root || !root.activeChannelId) return true
      const map = root.messagesByChannel || {}
      const all = (map[root.activeChannelId] || []).filter(
        (m) =>
          !m.threadRoot &&
          ((m.body && m.body.trim()) || (m.attachments && m.attachments.length))
      )
      return all.length <= _getVisible(root, root.activeChannelId)
    },
    tag: 'button',
    type: 'button',
    attr: { 'data-load-more': 'true' },
    alignSelf: 'center',
    padding: 'Z A',
    round: 'C',
    borderWidth: '1px',
    borderColor: 'line',
    borderStyle: 'solid',
    background: 'overlay',
    color: 'caption',
    cursor: 'pointer',
    fontSize: 'Y2',
    fontWeight: '600',
    marginBottom: 'Z',
    ':hover': { background: 'overlayStrong', color: 'title' },
    text: (el, s) => {
      const root = s && s.root
      if (!root) return ''
      const chId = root.activeChannelId
      const map = root.messagesByChannel || {}
      const total = (map[chId] || []).filter(
        (m) =>
          !m.threadRoot &&
          ((m.body && m.body.trim()) || (m.attachments && m.attachments.length))
      ).length
      const visible = _getVisible(root, chId)
      return `Load ${Math.min(_PAGE_SIZE, total - visible)} earlier messages`
    },
    onClick: (e, el, s) => {
      const root = s && s.root
      if (!root) return
      const chId = root.activeChannelId
      const map = root.messagesByChannel || {}
      const total = (map[chId] || []).filter((m) => !m.threadRoot).length
      const cur = _getVisible(root, chId)
      const next = Math.min(cur + _PAGE_SIZE, total)
      const prevScroll = el.parent?.node?.scrollHeight || 0
      root.update({
        chatVisibleCounts: { ...(root.chatVisibleCounts || {}), [chId]: next }
      })
      // Preserve scroll position after prepending older messages.
      requestAnimationFrame(() => {
        const container = el.parent?.node
        if (container) {
          const newScroll = container.scrollHeight
          container.scrollTop = newScroll - prevScroll
        }
      })
    }
  },

  Spacer: { flex: '1 1 auto' },

  List: {
    flow: 'y',
    gap: '0',
    childExtends: 'ChatMessageRow',
    childrenAs: 'state',
    children: (el, s) => {
      const root = s && s.root
      if (!root || !root.activeChannelId) return []
      const map = root.messagesByChannel || {}
      const flat = (map[root.activeChannelId] || []).filter(
        (m) =>
          !m.threadRoot &&
          ((m.body && m.body.trim()) || (m.attachments && m.attachments.length))
      )
      const filtered = applyFilters(flat, root.filters)
      // Paginate: show only the last N messages.
      const visible = _getVisible(root, root.activeChannelId)
      const paged =
        filtered.length > visible
          ? filtered.slice(filtered.length - visible)
          : filtered
      return groupMessages(paged)
    }
  }
}

const _isMine = (s) =>
  !!(s && s.root && s.authorEmail && s.authorEmail === s.root.currentUserEmail)

export const ChatMessageRow = {
  flow: 'y',
  gap: '0',
  position: 'relative',
  contentVisibility: 'auto',
  containIntrinsicSize: 'auto 80px',
  marginTop: (el, s) => (s && s._grouped ? 'V' : 'A'),
  attr: {
    'data-msg-id': (el, s) => String((s && s.id) || ''),
    'data-mine': (el, s) => (_isMine(s) ? 'true' : 'false'),
    id: (el, s) => (s && s.id ? `msg-${s.id}` : null)
  },

  // Day separator pill — only the first message of a new day renders one.
  // The flag is set by groupMessages on the children() preprocessing pass.
  DaySeparator: {
    if: (el, s) => !!(s && s._newDay),
    flow: 'x',
    align: 'center center',
    gap: 'A',
    padding: 'A B Z B',

    LineL: { flex: '1', height: '1px', background: 'line', opacity: '.5' },
    Pill: {
      tag: 'span',
      padding: 'V A',
      round: 'F',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      background: 'overlayStrong',
      color: 'caption',
      fontSize: 'Y2',
      fontWeight: '700',
      letterSpacing: '.04em',
      text: (el, s) =>
        s && s.createdAt ? el.call('dayLabel', s.createdAt) : ''
    },
    LineR: { flex: '1', height: '1px', background: 'line', opacity: '.5' }
  },

  Row: {
    flow: 'x',
    gap: 'A',
    align: 'flex-start left',
    flexDirection: (el, s) => (_isMine(s) ? 'row-reverse' : 'row'),
    // Show hover-time when the bubble row is hovered.
    onCreate: (el) => {
      if (!el.node) return
      const row = el.node.closest('[data-msg-id]') || el.node
      row.addEventListener('mouseenter', () => {
        const time = el.node.querySelector('[data-hover-time]')
        if (time) time.style.opacity = '1'
      })
      row.addEventListener('mouseleave', () => {
        const time = el.node.querySelector('[data-hover-time]')
        if (time) time.style.opacity = '0'
      })
    },
    // Tighter top padding for grouped continuation rows so a same-author
    // run reads as one stacked unit instead of separate cards.
    padding: (el, s) => (s && s._grouped ? 'V B 0 B' : 'A B 0 B'),
    position: 'relative',
    transition: 'background 120ms ease',
    ':hover': { background: 'overlay' },

    // Fixed-width avatar gutter — keeps continuation messages perfectly
    // aligned even when the avatar is hidden.
    AvatarSlot: {
      flow: 'y',
      align: 'flex-start center',
      flex: '0 0 auto',
      width: 'C',
      paddingTop: 'V',

      Avatar: {
        if: (el, s) => !(s && s._grouped),
        avatarType: 'initials',
        boxSize: 'C C',
        round: '50%',
        borderWidth: '1.5px',
        borderColor: 'line',
        borderStyle: 'solid',
        boxShadow: '0 1px 3px var(--color-shadow)',
        cursor: 'pointer',
        attr: {
          'data-personrow': 'true',
          'data-email': (el, s) => (s && s.authorEmail) || ''
        },
        src: (el, s) =>
          el.call(
            'avatarUrl',
            (s && el.call('findPerson', s.authorEmail)?.name) ||
              (s && s.authorEmail)
          )
      }
    },

    Content: {
      flex: '1 1 0',
      flow: 'y',
      gap: 'V',
      minWidth: '0',
      align: (el, s) => (_isMine(s) ? 'flex-end' : 'flex-start'),

      // Author name — only first message of a group.
      Author: {
        if: (el, s) => !(s && s._grouped),
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        letterSpacing: '-.005em',
        color: 'title',
        marginBottom: 'V',
        text: (el, s) => {
          const p = s && el.call('findPerson', s.authorEmail)
          return (p && p.name) || (s && s.authorEmail) || 'unknown'
        }
      },

      // Body + hover-time row — time appears next to the bubble on hover.
      BodyRow: {
        hide: (el, s) => !(s && s.body),
        flow: 'x',
        gap: 'Z',
        align: 'center',
        flexDirection: (el, s) => (_isMine(s) ? 'row-reverse' : 'row'),

        Body: {
          tag: 'div',
          maxWidth: 'I',
          padding: 'Z1 A',
          // Stacked bubbles get tighter corners on the connecting edge.
          // First message: large top corners, small bottom-tail corner.
          // Middle (grouped): small all around.
          // The _grouped flag from groupMessages drives this.
          round: (el, s) => {
            const mine = _isMine(s)
            if (s && s._grouped) {
              // Continuation bubble — tight corners on the stacking side.
              return mine ? 'Z A1 Z A1' : 'A1 Z A1 Z'
            }
            // First in group — standard asymmetric tail.
            return mine ? 'A1 A1 Z A1' : 'A1 A1 A1 Z'
          },
          background: (el, s) => (_isMine(s) ? 'blue.12' : 'white.08'),
          color: 'title',
          fontSize: 'Z2',
          lineHeight: '1.5',
          wordBreak: 'break-word',
          opacity: (el, s) => (s && s._optimistic ? '0.55' : '1'),
          onCreate: (el, s) =>
            renderMarkdown(
              el,
              s && s.body,
              null,
              s && s.root && s.root.people,
              s && s.root && s.root.channels
            ),
          onUpdate: (el, s) =>
            renderMarkdown(
              el,
              s && s.body,
              null,
              s && s.root && s.root.people,
              s && s.root && s.root.channels
            )
        },

        Time: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          opacity: '0',
          transition: 'opacity 120ms ease',
          whiteSpace: 'nowrap',
          attr: { 'data-hover-time': 'true' },
          text: (el, s) =>
            s && s.createdAt ? el.call('clockTime', s.createdAt) : ''
        }
      },

      // Reaction pills below the bubble.
      Reactions: {
        flow: 'x wrap',
        gap: 'V',
        display: (el, s) => {
          const r = s && s.reactions
          return r && Object.keys(r).length > 0 ? 'flex' : 'none'
        },
        flexDirection: (el, s) => (_isMine(s) ? 'row-reverse' : 'row'),
        childExtends: 'ChatReactionPill',
        childrenAs: 'state',
        children: (el, s) => {
          const r = s && s.reactions
          if (!r) return []
          return Object.entries(r).map(([emoji, users]) => ({
            emoji,
            users,
            count: users.length,
            isMine: users.includes(s.root?.currentUserEmail),
            msgId: s.id
          }))
        }
      },

      // Attachments grid: images render as thumbnails, others as file chips.
      Attachments: {
        flow: 'x wrap',
        gap: 'Z',
        if: (el, s) => !!(s && s.attachments && s.attachments.length),
        childExtends: 'ChatAttachmentRender',
        childrenAs: 'state',
        children: (el, s) => s.attachments
      },

      // Thread reply count chip — styled as a clickable inline pill that
      // sits flush under the message body, matching Slack's inline thread
      // affordance.
      ThreadLink: {
        hide: (el, s) => !(s && parseInt(s.threadCount, 10) > 0),
        tag: 'button',
        type: 'button',
        flow: 'x',
        align: 'center left',
        gap: 'Y',
        padding: 'V Z1',
        round: 'C',
        border: 'none',
        background: 'blue.1',
        color: 'blue',
        cursor: 'pointer',
        fontSize: 'Y2',
        fontWeight: '600',
        alignSelf: (el, s) => (_isMine(s) ? 'flex-end' : 'flex-start'),
        marginTop: 'V',
        ':hover': { background: 'blue.2' },
        transition: 'background 120ms ease',
        attr: { 'data-thread-open': (el, s) => String((s && s.id) || '') },
        ThreadIcon: { extends: 'Icon', icon: 'chat', boxSize: 'Z' },
        Text: {
          tag: 'span',
          text: (el, s) => {
            const n = (s && parseInt(s.threadCount, 10)) || 0
            return `${n} repl${n === 1 ? 'y' : 'ies'}`
          }
        }
      }
    },

    // Small avatars of members who have read up to this message.
    SeenAvatars: {
      flow: 'x',
      gap: 'V',
      padding: '0 0 0 D1',
      hide: (el, s) => {
        if (!s?.id || !s?.root) return true
        const cid = s.root.activeChannelId
        const members = (s.root.channelMembers || []).filter(
          (m) =>
            m.channel_id === cid && m.member_email !== s.root.currentUserEmail
        )
        // Find members whose last_read_at falls on or after this message
        const msgTime = s.createdAt ? new Date(s.createdAt).getTime() : 0
        if (!msgTime) return true
        // Get all messages in channel to find the next message
        const msgs = (s.root.messagesByChannel?.[cid] || []).filter(
          (m) => !m.threadRoot
        )
        const idx = msgs.findIndex((m) => m.id === s.id)
        const nextTime =
          idx >= 0 && idx < msgs.length - 1
            ? new Date(msgs[idx + 1].createdAt).getTime()
            : Infinity
        const seenBy = members.filter((m) => {
          const readAt = m.last_read_at ? new Date(m.last_read_at).getTime() : 0
          return readAt >= msgTime && readAt < nextTime
        })
        return seenBy.length === 0
      },
      childExtends: {
        avatarType: 'initials',
        boxSize: 'Z2',
        round: '50%',
        title: (el, s) => s._seenName || '',
        src: (el, s) => el.call('avatarUrl', s._seenName)
      },
      childrenAs: 'state',
      children: (el, s) => {
        if (!s?.id || !s?.root) return []
        const cid = s.root.activeChannelId
        const members = (s.root.channelMembers || []).filter(
          (m) =>
            m.channel_id === cid && m.member_email !== s.root.currentUserEmail
        )
        const msgTime = s.createdAt ? new Date(s.createdAt).getTime() : 0
        if (!msgTime) return []
        const msgs = (s.root.messagesByChannel?.[cid] || []).filter(
          (m) => !m.threadRoot
        )
        const idx = msgs.findIndex((m) => m.id === s.id)
        const nextTime =
          idx >= 0 && idx < msgs.length - 1
            ? new Date(msgs[idx + 1].createdAt).getTime()
            : Infinity
        const people = s.root.people || []
        return members
          .filter((m) => {
            const readAt = m.last_read_at
              ? new Date(m.last_read_at).getTime()
              : 0
            return readAt >= msgTime && readAt < nextTime
          })
          .map((m) => {
            const person = people.find((p) => p.email === m.member_email)
            return { _seenName: person?.name || m.member_email }
          })
      }
    },

  }
}

// Helper: is this a media type that can be previewed in a lightbox?
const _isMedia = (mime) =>
  mime && (mime.startsWith('image/') || mime.startsWith('video/'))

// Renders one attachment — media files show as thumbnails that open a
// lightbox on click, other files show as download chips.
export const ChatReactionPill = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center center',
  gap: 'V',
  padding: 'V Z',
  round: 'B',
  cursor: 'pointer',
  fontSize: 'Y2',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: (el, s) => (s && s.isMine ? 'blue.4' : 'line'),
  background: (el, s) => (s && s.isMine ? 'blue.08' : 'transparent'),
  color: 'title',
  ':hover': { background: 'overlay' },
  transition: 'background 100ms ease',
  Emoji: { tag: 'span', text: (el, s) => s.emoji },
  Count: {
    tag: 'span',
    fontSize: 'Y1',
    fontWeight: '600',
    color: (el, s) => (s && s.isMine ? 'blue' : 'caption'),
    text: (el, s) => String(s.count || 0)
  },
  onClick: (e, el, s) => {
    e.stopPropagation()
    if (!s || !s.msgId) return
    el.call('toggleReaction', s.msgId, s.emoji)
  }
}

export const ChatReactionEmoji = {
  tag: 'button',
  align: 'center center',
  width: 'B',
  height: 'B',
  round: 'Z',
  background: 'transparent',
  borderWidth: '0',
  fontSize: 'A',
  cursor: 'pointer',
  text: (el, s) => s.emoji,
  ':hover': { background: 'overlay' },
  onClick: (ev, el, s) => {
    ev.preventDefault()
    ev.stopPropagation()
    const msgId = _activeMsgId(el)
    if (!msgId || !s?.emoji) return
    el.call('toggleReaction', msgId, s.emoji)
    const popover = el.node.closest('[data-arp-popover]')
    if (popover) {
      popover.style.opacity = '0'
      popover.style.pointerEvents = 'none'
    }
  }
}

export const ChatAttachmentRender = {
  round: 'Z',
  overflow: 'hidden',
  maxWidth: 'I',
  cursor: 'pointer',
  transition: 'opacity 120ms ease',
  ':hover': { opacity: '.85' },
  onClick: (e, el, s) => {
    if (!s || !s.url) return
    if (_isMedia(s.mime)) {
      s.root.update({ lightbox: { url: s.url, mime: s.mime, name: s.name } })
    } else {
      window.open(s.url, '_blank')
    }
  },

  // Image thumbnail
  ImageThumb: {
    tag: 'img',
    width: '100%',
    maxHeight: 'G',
    round: 'Z',
    objectFit: 'cover',
    hide: (el, s) => !(s && s.mime && s.mime.startsWith('image/')),
    src: (el, s) => s.url,
    alt: (el, s) => s.name || 'image'
  },

  // Video thumbnail
  VideoThumb: {
    tag: 'video',
    width: '100%',
    maxHeight: 'G',
    round: 'Z',
    objectFit: 'cover',
    hide: (el, s) => !(s && s.mime && s.mime.startsWith('video/')),
    src: (el, s) => s.url,
    preload: 'metadata'
  },

  // Non-media file chip
  FileChip: {
    hide: (el, s) => !!(s && _isMedia(s.mime)),
    flow: 'x',
    align: 'center left',
    gap: 'V',
    padding: 'V Z',
    borderWidth: '1px',
    borderColor: 'line',
    borderStyle: 'solid',
    background: 'document',
    ':hover': { background: 'overlay' },

    Icon: {
      tag: 'span',
      fontSize: 'A',
      text: (el, s) => {
        if (!s) return '📄'
        const m = s.mime || ''
        if (m.includes('pdf')) return '📕'
        if (m.includes('zip') || m.includes('archive')) return '🗜'
        return '📄'
      }
    },
    Info: {
      flow: 'y',
      gap: 'V',
      minWidth: '0',
      Name: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'title',
        fontWeight: '600',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => (s && s.name) || ''
      },
      Size: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => el.call('formatBytes', s && s.size)
      }
    }
  }
}

const _ARP_EMOJIS = [
  '👍','❤️','🎉','🚀','🔥','👏','👀','😂','😍','🤔','😮','😢',
  '😡','✅','❌','💯','🙏','💪','🫡','🤝','👋','🎯','💡','⭐'
]

const _QUICK_REACTS = ['\uD83D\uDC4D', '\u2764\uFE0F', '\uD83D\uDE02', '\uD83D\uDC40']

const _activeMsgId = (el) => {
  const v = el.node.closest('[data-hover-actions]')?.getAttribute('data-active-msg')
  const id = v ? parseInt(v, 10) : NaN
  return Number.isFinite(id) ? id : null
}

const _closeParentPopover = (el) => _togglePopover(el.node.closest('[data-ha-popover]'), false)

const _togglePopover = (node, open) => {
  if (!node) return
  const isOpen = open ?? node.style.opacity !== '1'
  node.style.opacity = isOpen ? '1' : '0'
  node.style.pointerEvents = isOpen ? 'auto' : 'none'
  if (isOpen) {
    requestAnimationFrame(() => {
      const rect = node.getBoundingClientRect()
      if (rect.right > window.innerWidth) {
        node.style.left = 'auto'
        node.style.right = '0'
      }
      if (rect.bottom > window.innerHeight) {
        node.style.top = 'auto'
        node.style.bottom = '100%'
      }
    })
  }
}

const _getMsgBody = (msgId, root) => {
  const list = (root?.messagesByChannel || {})[root?.activeChannelId] || []
  return list.find(m => m.id === msgId)?.body || ''
}

const _titleFromBody = (body, fallback) =>
  (body.split('\n')[0] || fallback).slice(0, 120)

// Gather ±10 messages around the target for thread context.
const _getChatContext = (msgId, root) => {
  const list = (root?.messagesByChannel || {})[root?.activeChannelId] || []
  const idx = list.findIndex(m => m.id === msgId)
  if (idx === -1) return ''
  const start = Math.max(0, idx - 10)
  const end = Math.min(list.length, idx + 11)
  return list.slice(start, end).map(m => {
    const person = (root.people || []).find(p => p.email === m.authorEmail)
    const name = person?.name || m.authorEmail || 'unknown'
    const marker = m.id === msgId ? ' [THIS MESSAGE]' : ''
    return `${name}${marker}: ${(m.body || '').trim()}`
  }).join('\n')
}

// AI-prefill: sends chat context to AI to generate structured ticket/note fields.
const _aiPrefill = async (root, msgId, type, el) => {
  const context = _getChatContext(msgId, root)
  const body = _getMsgBody(msgId, root)
  const today = new Date().toISOString().slice(0, 10)

  const prompts = {
    ticket: `Based on this chat conversation, create a ticket. Extract: title (short), body (description), priority (low/medium/high/urgent/critical), issueType (bug/feature/user-story/subtask), acceptanceCriteria, stepsToReproduce (if bug). For dueDate only use today's date ${today} if explicitly mentioned. Return ONLY valid JSON.`,
    note: `Based on this chat conversation, create a note. Extract: title (short summary), content (key points from the discussion). Return ONLY valid JSON with {title, content}.`,
    kb: `Based on this chat conversation, create a knowledge base article. Extract: title (topic), content (structured explanation of the topic discussed). Return ONLY valid JSON with {title, content}.`,
    event: `Based on this chat conversation, create a calendar event. Extract: title (meeting/event name), description (agenda/context). For dates only use today ${today}. Return ONLY valid JSON with {title, description}.`
  }

  try {
    // Routes through the workspace wrapper's /ai/chat endpoint via the
    // SDK entity dispatcher. The wrapper buffers the upstream SSE stream
    // and returns the aggregated text; streaming UX is intentionally lost
    // here in exchange for unified auth + tenant scoping.
    const result = await getSDK().execute('workspaceProject.aiChat', 'rpc', {
      messages: [
        { role: 'system', content: prompts[type] || prompts.ticket },
        { role: 'user', content: `Chat thread:\n${context}\n\nTarget message: ${body}` }
      ]
    })
    const full = result?.text || result?.data?.text || ''
    if (!full) return null

    const m = full.match(/\{[\s\S]*\}/)
    if (!m) return null
    // AI may append text after JSON — try parsing, then truncate on failure
    try { return JSON.parse(m[0]) } catch {
      // Find balanced braces
      let depth = 0, end = 0
      for (let i = 0; i < m[0].length; i++) {
        if (m[0][i] === '{') depth++
        if (m[0][i] === '}') { depth--; if (depth === 0) { end = i + 1; break } }
      }
      try { return JSON.parse(m[0].slice(0, end)) } catch { return null }
    }
  } catch (err) {
    console.error('[_aiPrefill]', err)
    return null
  }
}

export const ChatHoverActions = {
  attr: { 'data-hover-actions': 'true' },
  position: 'fixed',
  top: '0',
  left: '0',
  flow: 'x',
  padding: 'V',
  round: 'Z',
  background: 'overlayStrong',
  backdropFilter: 'blur(8px)',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  zIndex: '100',
  boxShadow: '0 4px 12px var(--color-shadow)',
  opacity: '0',
  pointerEvents: 'none',
  transition: 'opacity 120ms ease',
  fontSize: 'Z1',

  onCreate: (el) => {
    if (!el || !el.node) return
    const node = el.node
    node.addEventListener('mouseenter', () => {
      node.setAttribute('data-hover-lock', 'true')
      node.style.opacity = '1'
      node.style.pointerEvents = 'auto'
    })
    node.addEventListener('mouseleave', () => {
      node.removeAttribute('data-hover-lock')
      node.style.opacity = '0'
      node.style.pointerEvents = 'none'
      // Close any open sub-popovers on leave.
      node.querySelectorAll('[data-ha-popover]').forEach(p => _togglePopover(p, false))
    })
  },

  // Quick reactions (outside).
  QuickReacts: {
    flow: 'x',
    childExtends: 'ChatHoverQuickReact',
    childrenAs: 'state',
    children: () => _QUICK_REACTS.map(emoji => ({ emoji }))
  },

  MoreReactBtn: {
    extends: 'ChatHoverIconBtn',
    position: 'relative',
    title: 'More reactions',
    aria: { label: 'More reactions' },
    Icon: { icon: 'add', boxSize: 'A', pointerEvents: 'none' },
    onClick: (e, el) => {
      e.stopPropagation()
      _togglePopover(el.node.querySelector('[data-ha-popover="emoji"]'))
    },
    ArpPopover: {
      flow: 'y', gap: 'Z1', position: 'absolute', top: '100%', left: '0',
      zIndex: '200', width: 'G', maxWidth: '90vw', maxHeight: '50vh',
      overflow: 'auto', padding: 'Z1', round: 'Z',
      background: 'overlayStrong',
      borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
      boxShadow: '0 4px 12px var(--color-shadow)',
      opacity: '0', pointerEvents: 'none', transition: 'opacity 120ms ease',
      attr: { 'data-ha-popover': 'emoji' },
      EmojiGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 'V',
        childExtends: 'ChatReactionEmoji', childrenAs: 'state',
        children: () => _ARP_EMOJIS.map(emoji => ({ emoji }))
      }
    }
  },

  Divider_1: { extends: 'ChatHoverDivider' },

  ThreadBtn: {
    extends: 'ChatHoverIconBtn',
    title: 'Reply in thread',
    aria: { label: 'Reply in thread' },
    Icon: { icon: 'chat', boxSize: 'A', pointerEvents: 'none' },
    onClick: (e, el, s) => {
      e.stopPropagation()
      const msgId = _activeMsgId(el)
      const root = s?.root
      if (!msgId || !root) return
      root.update({ activeThreadRootId: msgId })
    }
  },

  ReplyBtn: {
    extends: 'ChatHoverIconBtn',
    title: 'Reply',
    aria: { label: 'Reply' },
    Icon: { icon: 'reply', boxSize: 'A', pointerEvents: 'none' },
    onClick: (e, el, s) => {
      e.stopPropagation()
      const msgId = _activeMsgId(el)
      const root = s?.root
      if (!msgId || !root) return
      root.update({ composerReplyToMsgId: msgId })
      const composer = document.querySelector(
        '[data-key="ChannelColumn"] [data-key="Footer"] [data-key="Editor"], [data-key="ChannelColumn"] [data-key="Footer"] [contenteditable="true"], [data-key="ChannelColumn"] [data-key="Footer"] textarea'
      )
      composer?.focus()
    }
  },

  Divider_2: { extends: 'ChatHoverDivider' },

  MoreMenuBtn: {
    extends: 'ChatHoverIconBtn',
    position: 'relative',
    title: 'More actions',
    aria: { label: 'More actions' },
    Icon: { icon: 'more', boxSize: 'A', pointerEvents: 'none' },
    onClick: (e, el) => {
      e.stopPropagation()
      _togglePopover(el.node.querySelector('[data-ha-popover="menu"]'))
    },

    MenuPopover: {
      flow: 'y', gap: '0', position: 'absolute', top: '100%', right: '0',
      zIndex: '200', minWidth: 'F', padding: 'V', round: 'Z',
      background: 'overlayStrong',
      borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',
      boxShadow: '0 4px 12px var(--color-shadow)',
      opacity: '0', pointerEvents: 'none', transition: 'opacity 120ms ease',
      attr: { 'data-ha-popover': 'menu' },

      CreateTicket: {
        extends: 'ChatHoverMenuItem',
        Icon: { icon: 'checkmark' },
        Label: { text: 'Create ticket' },
        onClick: async (e, el, s) => {
          e.stopPropagation()
          const msgId = _activeMsgId(el); const root = s?.root
          if (!msgId || !root) return
          const body = _getMsgBody(msgId, root)
          _closeParentPopover(el)
          // Open modal immediately with basic content + loading indicator
          root.update({
            selectedTicket: { _new: true },
            ticketDraft: el.call('freshTicket', { title: _titleFromBody(body, ''), body }),
            ticketEditMode: true,
            _aiTicketLoading: true
          })
          // AI-prefill from chat context in background
          const ai = await _aiPrefill(root, msgId, 'ticket', el)
          if (ai) {
            const draft = root.ticketDraft || {}
            root.update({ ticketDraft: { ...draft, ...ai } })
          }
          root.update({ _aiTicketLoading: false })
        }
      },

      CreateEvent: {
        extends: 'ChatHoverMenuItem',
        Icon: { icon: 'date' },
        Label: { text: 'Create event' },
        onClick: async (e, el, s) => {
          e.stopPropagation()
          const msgId = _activeMsgId(el); const root = s?.root
          if (!msgId || !root) return
          const body = _getMsgBody(msgId, root)
          _closeParentPopover(el)
          const ai = await _aiPrefill(root, msgId, 'event', el)
          el.call('saveEventToSupabase', {
            title: ai?.title || _titleFromBody(body, 'Untitled event'),
            description: ai?.description || body,
            start_time: new Date().toISOString(),
            end_time: new Date(Date.now() + 3600000).toISOString(),
            type: 'meeting',
            author: root.currentUserEmail
          })
        }
      },

      CreateNote: {
        extends: 'ChatHoverMenuItem',
        Icon: { icon: 'document' },
        Label: { text: 'Create note' },
        onClick: async (e, el, s) => {
          e.stopPropagation()
          const msgId = _activeMsgId(el); const root = s?.root
          if (!msgId || !root) return
          const body = _getMsgBody(msgId, root)
          _closeParentPopover(el)
          const ai = await _aiPrefill(root, msgId, 'note', el)
          el.call('createNote', {
            title: ai?.title || _titleFromBody(body, 'Untitled note'),
            content: ai?.content || body,
            tags: ['chat']
          })
        }
      },

      CreateKB: {
        extends: 'ChatHoverMenuItem',
        Icon: { icon: 'star' },
        Label: { text: 'Create knowledge base' },
        onClick: async (e, el, s) => {
          e.stopPropagation()
          const msgId = _activeMsgId(el); const root = s?.root
          if (!msgId || !root) return
          const body = _getMsgBody(msgId, root)
          _closeParentPopover(el)
          const ai = await _aiPrefill(root, msgId, 'kb', el)
          el.call('createKnowledgeArticle', {
            title: ai?.title || _titleFromBody(body, 'Untitled article'),
            content: ai?.content || body
          })
        }
      },

      Bookmark: {
        extends: 'ChatHoverMenuItem',
        Icon: { icon: 'bookmark' },
        Label: { text: 'Bookmark' },
        onClick: (e, el, s) => {
          e.stopPropagation()
          const msgId = _activeMsgId(el)
          if (!msgId || !s?.root) return
          el.call('toggleChatBookmark', msgId)
          _closeParentPopover(el)
        }
      },

      Pin: {
        extends: 'ChatHoverMenuItem',
        Icon: { icon: 'pin' },
        Label: { text: 'Pin' },
        onClick: (e, el, s) => {
          e.stopPropagation()
          const msgId = _activeMsgId(el)
          if (!msgId || !s?.root) return
          el.call('toggleChatPin', msgId)
          _closeParentPopover(el)
        }
      },

      CopyURL: {
        extends: 'ChatHoverMenuItem',
        Icon: { icon: 'link' },
        Label: { text: 'Copy URL' },
        onClick: (e, el) => {
          e.stopPropagation()
          const msgId = _activeMsgId(el)
          if (!msgId) return
          navigator.clipboard?.writeText(`${window.location.origin}${window.location.pathname}#msg-${msgId}`)
          _closeParentPopover(el)
        }
      },

      Forward: {
        extends: 'ChatHoverMenuItem',
        Icon: { icon: 'arrowRight' },
        Label: { text: 'Forward' },
        onClick: (e, el, s) => {
          e.stopPropagation()
          const msgId = _activeMsgId(el); const root = s?.root
          if (!msgId || !root) return
          navigator.clipboard?.writeText(_getMsgBody(msgId, root))
          _closeParentPopover(el)
        }
      }
    }
  }
}

export const ChatHoverIconBtn = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center center',
  padding: 'Z Z1',
  round: 'V',
  border: 'none',
  background: 'transparent',
  color: 'subtitle',
  cursor: 'pointer',
  ':hover': { background: 'line', color: 'title' },
  transition: 'background 100ms ease'
}

export const ChatHoverQuickReact = {
  extends: 'ChatHoverIconBtn',
  padding: 'Z',
  fontSize: 'Z',
  lineHeight: '1',
  text: (el, s) => s.emoji,
  ':hover': { background: 'line', transform: 'scale(1.2)' },
  transition: 'transform 100ms ease',
  onClick: (e, el, s) => {
    e.stopPropagation()
    const msgId = _activeMsgId(el)
    if (msgId && s?.emoji) el.call('toggleReaction', msgId, s.emoji)
  }
}

export const ChatHoverDivider = {
  width: '1px',
  height: 'A',
  background: 'line',
  alignSelf: 'center',
  margin: '0 Y'
}

export const ChatHoverMenuItem = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  padding: 'Z A',
  round: 'V',
  border: 'none',
  background: 'transparent',
  color: 'title',
  cursor: 'pointer',
  fontSize: 'Z2',
  textAlign: 'left',
  width: '100%',
  ':hover': { background: 'line' },
  transition: 'background 100ms ease',
  Icon: { boxSize: 'A', flex: '0 0 auto', pointerEvents: 'none' },
  Label: { tag: 'span', flex: '1 1 auto' }
}
